import { DialogComponent } from '@theme/dialog';
import { enableSheetDrag } from '@theme/sheet-drag';

/**
 * PLW slide-out overlay.
 *
 * Extends Horizon's {@link DialogComponent}, so it inherits the native
 * `<dialog showModal>` machinery: top-layer rendering + focus trap, body
 * scroll-lock, outside-click close, ESC close, `::backdrop` dimming, and the
 * `dialog:open` / `dialog:close` events. The slide direction (right rail on
 * desktop, bottom sheet on mobile) is pure CSS — see `.plw-overlay` in base.css.
 *
 * Adds on top of the base:
 * - Mobile drag-to-dismiss on the top handle (swipe the sheet down to close).
 * - Optional interior tabs: if the overlay contains a `[role="tablist"]`, its
 *   `[role="tab"]` buttons are wired to their `aria-controls` `[role="tabpanel"]`s.
 *
 * Trigger from anywhere with `<button on:click="#<id>/showDialog">`.
 *
 * @typedef {object} OverlayRefs
 * @property {HTMLDialogElement} dialog - The dialog element (from DialogComponent).
 * @property {HTMLElement} [handle] - The mobile drag handle.
 *
 * @extends DialogComponent
 */
class PlwOverlay extends DialogComponent {
  #mobile = matchMedia('(max-width: 749px)');
  /** @type {{ reset: () => void, destroy: () => void } | null} */
  #drag = null;

  connectedCallback() {
    super.connectedCallback();

    const { dialog, handle } = /** @type {OverlayRefs} */ (this.refs);
    if (dialog && handle) {
      // Shared bottom-sheet drag-to-dismiss (also used by the cart drawer).
      // `surface: dialog` makes the whole pane draggable, not just the handle.
      this.#drag = enableSheetDrag({
        dialog,
        handle,
        surface: dialog,
        isMobile: () => this.#mobile.matches,
        onDismiss: () => this.closeDialog(),
        dragVar: '--plw-overlay-drag',
      });
      // Clear drag-leftover inline styles once closed, so the next open is clean.
      this.addEventListener('dialog:close', this.#drag.reset);
    }

    this.#initTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#drag) {
      this.removeEventListener('dialog:close', this.#drag.reset);
      this.#drag.destroy();
      this.#drag = null;
    }
  }

  /* ---- Optional interior tabs ----
     Each [role="tablist"] is wired independently, so an overlay can carry a
     secondary switcher (e.g. the size guide's inches|cm unit toggle) without
     the groups deselecting each other. */

  #initTabs() {
    for (const tablist of this.querySelectorAll('[role="tablist"]')) {
      const tabs = /** @type {HTMLElement[]} */ (Array.from(tablist.querySelectorAll('[role="tab"]')));
      if (!tabs.length) continue;
      for (const tab of tabs) {
        tab.addEventListener('click', () => this.#selectTab(tabs, tab));
        tab.addEventListener('keydown', (event) => this.#onTabKeydown(event, tabs));
      }
    }
  }

  /**
   * @param {HTMLElement[]} tabs - The tab group the tab belongs to.
   * @param {HTMLElement} tab
   */
  #selectTab(tabs, tab) {
    for (const candidate of tabs) {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;
      const panelId = candidate.getAttribute('aria-controls');
      const panel = panelId && document.getElementById(panelId);
      if (panel) panel.hidden = !selected;
    }
    tab.focus();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement[]} tabs - The tab group being navigated.
   */
  #onTabKeydown(event, tabs) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();

    const current = /** @type {HTMLElement} */ (event.currentTarget);
    const index = tabs.indexOf(current);
    if (index === -1) return;

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const next = tabs[(index + direction + tabs.length) % tabs.length];
    this.#selectTab(tabs, next);
  }
}

if (!customElements.get('plw-overlay')) customElements.define('plw-overlay', PlwOverlay);
